import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthFetch } from '../lib/useAuthFetch'
import { useBrandArea, type BrandRow, type BrandVerdict } from '../hooks/useBrandArea'
import {
  Card,
  SectionLabel,
  Pill,
  StatTile,
  DataTable,
  DecisionSummary,
  type Column,
  type DecisionPriority,
} from '../components/ui'

const API = import.meta.env.VITE_API_URL ?? ''

interface Location { id: string; name: string }

// Ubiquity thresholds — data, not advice. ≥⅔ of nearby shops = commodity;
// ≤⅓ = rare/differentiation. The band between is left unlabeled on purpose.
const COMMODITY = 0.66
const RARE = 0.34
const PARITY = 0.5

type SortMode = 'common' | 'rare' | 'price'

const VERDICT_TONE: Record<BrandVerdict, 'danger' | 'accent' | 'neutral'> = {
  higher: 'danger',
  cheaper: 'accent',
  parity: 'neutral',
  no_own_price: 'neutral',
}
const VERDICT_LABEL: Record<BrandVerdict, string> = {
  higher: "You're higher",
  cheaper: "You're cheaper",
  parity: 'At parity',
  no_own_price: 'Not carried',
}

// Your standing vs one reference price.
function youVerdict(your: number | null, ref: number): BrandVerdict {
  if (your == null) return 'no_own_price'
  if (your > ref + PARITY) return 'higher'
  if (your < ref - PARITY) return 'cheaper'
  return 'parity'
}

function ubiquityTone(u: number): { label: string; color: string; bg: string } {
  if (u >= COMMODITY) return { label: 'carried by most rivals', color: 'var(--warm)', bg: 'rgba(186,117,23,0.10)' }
  if (u <= RARE) return { label: 'rare in this area', color: 'var(--accent)', bg: 'rgba(11,184,184,0.10)' }
  return { label: 'mixed coverage', color: 'var(--text-3)', bg: 'var(--surface-3)' }
}

function exportCSV(brands: BrandRow[], M: number, locationName: string) {
  const header = ['Brand', `Carried (of ${M})`, 'Ubiquity %', 'Avg price', 'Min', 'Max', 'Your price', 'Standing'].join(',')
  const rows = brands.map((b) =>
    [
      `"${b.brand.replace(/"/g, '""')}"`,
      b.carrier_count,
      Math.round(b.ubiquity * 100),
      b.avg_price.toFixed(2),
      b.min_price.toFixed(2),
      b.max_price.toFixed(2),
      b.your_price != null ? b.your_price.toFixed(2) : '',
      VERDICT_LABEL[b.verdict],
    ].join(',')
  )
  const csv = [header, ...rows].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `brand-carriers-${locationName.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

// ── Ubiquity bar ──────────────────────────────────────────────────────────────
function UbiquityBar({ n, m, color }: { n: number; m: number; color: string }) {
  const pct = m > 0 ? (n / m) * 100 : 0
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
      <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--surface-3)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width .3s' }} />
      </div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, color: 'var(--text-2)', minWidth: 38, textAlign: 'right' }}>
        {n}/{m}
      </div>
    </div>
  )
}

// ── Brand list row ──────────────────────────────────────────────────────────
function BrandListRow({ b, m, selected, onClick }: { b: BrandRow; m: number; selected: boolean; onClick: () => void }) {
  const tone = ubiquityTone(b.ubiquity)
  return (
    <div
      onClick={onClick}
      style={{
        display: 'grid', gridTemplateColumns: '1fr 140px 96px', gap: 12, alignItems: 'center',
        padding: '11px 14px', borderRadius: 'var(--r-sm)', cursor: 'pointer',
        border: `1.5px solid ${selected ? 'var(--accent)' : 'transparent'}`,
        background: selected ? 'var(--surface-3)' : 'transparent',
        transition: 'background .12s, border-color .12s',
      }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = 'var(--surface-2)' }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = 'transparent' }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {b.brand}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 8.5, letterSpacing: '0.06em', textTransform: 'uppercase', color: tone.color, background: tone.bg, padding: '1px 6px', borderRadius: 3, fontWeight: 600 }}>
            {tone.label}
          </span>
          {b.any_on_sale && (
            <span style={{ fontFamily: 'var(--mono)', fontSize: 8.5, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--warm)', fontWeight: 600 }}>
              promo
            </span>
          )}
        </div>
      </div>
      <UbiquityBar n={b.carrier_count} m={m} color={tone.color} />
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>
          ${b.avg_price.toFixed(2)}
        </div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)' }}>
          ${b.min_price.toFixed(2)}–${b.max_price.toFixed(2)}
        </div>
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
export default function BrandCarrier() {
  const authFetch = useAuthFetch()
  const navigate = useNavigate()

  const [locations, setLocations] = useState<Location[]>([])
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null)
  const [radius, setRadius] = useState(10)
  const [category, setCategory] = useState('')
  const [sortMode, setSortMode] = useState<SortMode>('common')
  const [search, setSearch] = useState('')
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null)

  const { data, loading, error } = useBrandArea(selectedLocation, radius, category || undefined)

  useEffect(() => {
    authFetch(`${API}/api/v1/locations`)
      .then((r) => r.json())
      .then((d) => {
        const locs: Location[] = d.locations || []
        setLocations(locs)
        if (locs.length > 0 && !selectedLocation) setSelectedLocation(locs[0].id)
      })
      .catch(() => {})
  }, [authFetch])

  const M = data?.total_rivals ?? 0
  const allBrands = data?.brands ?? []
  // Category vocabulary is data-driven: flower size buckets (Flower 1g/3.5g/…) +
  // the non-flower categories that actually have data in range. Computed by the
  // API over every in-range SKU, so it's stable regardless of the active filter.
  const categoryOptions = data?.categories ?? []

  // Drop a stale flower/category selection when switching to a location/radius
  // where that bucket has no data (otherwise the <select> renders blank).
  useEffect(() => {
    if (category && categoryOptions.length > 0 && !categoryOptions.includes(category)) {
      setCategory('')
    }
  }, [category, categoryOptions])

  // Filter + sort (the API already sorts most-carried first; we re-sort per mode).
  const brands = useMemo(() => {
    let list = allBrands
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      list = list.filter((b) => b.brand.toLowerCase().includes(q))
    }
    const sorted = [...list]
    if (sortMode === 'common') sorted.sort((a, b) => b.carrier_count - a.carrier_count || a.avg_price - b.avg_price)
    else if (sortMode === 'rare') sorted.sort((a, b) => a.carrier_count - b.carrier_count || a.avg_price - b.avg_price)
    else sorted.sort((a, b) => b.avg_price - a.avg_price)
    return sorted
  }, [allBrands, search, sortMode])

  // Keep a valid selection as filters change.
  const selected = useMemo(
    () => brands.find((b) => b.brand_key === selectedBrand) ?? brands[0] ?? null,
    [brands, selectedBrand]
  )

  const commodityCount = allBrands.filter((b) => b.ubiquity >= COMMODITY).length
  const rareCount = allBrands.filter((b) => b.ubiquity <= RARE).length

  const locationName = locations.find((l) => l.id === selectedLocation)?.name || 'Location'

  // Non-carriers for the selected brand: shops in range that don't carry it.
  const nonCarriers = useMemo(() => {
    if (!data || !selected) return []
    const have = new Set(selected.carriers.map((c) => c.competitor_id))
    return data.rivals.filter((r) => !have.has(r.competitor_id))
  }, [data, selected])

  const sep = <div style={{ width: 1, height: 20, background: 'var(--border-2)', flexShrink: 0, margin: '0 4px' }} />

  const selStanding: BrandVerdict = selected ? selected.verdict : 'no_own_price'

  // ── Decision derivation for the selected brand (all numbers dynamic) ─────────
  // Coverage phrase: "all 16" when every nearby shop carries it, else "N of M".
  const coveragePhrase = selected
    ? (selected.carrier_count === M ? `all ${M}` : `${selected.carrier_count} of ${M}`)
    : ''
  const coveragePct = selected ? Math.round(selected.ubiquity * 100) : 0
  const priceGap = selected && selected.your_price != null ? selected.your_price - selected.avg_price : null

  // Priority: above the nearby average beyond the parity band = high (exposed);
  // below = opportunity; at parity = watch; not carried = insufficient evidence.
  let selPriority: DecisionPriority = 'watch'
  if (!selected || selected.your_price == null) selPriority = 'insufficient-data'
  else if (priceGap! > PARITY) selPriority = 'high'
  else if (priceGap! < -PARITY) selPriority = 'opportunity'
  else selPriority = 'watch'

  // Plain-language own-price position vs the nearby average. Ubiquity is stated
  // as coverage only — never as good/bad — per the decision contract.
  function pricePositionClause(sel: BrandRow): string {
    if (sel.your_price == null) return "you don't carry it, so there's no own-price comparison"
    const g = sel.your_price - sel.avg_price
    const amt = `$${Math.abs(g).toFixed(2)}`
    if (g > PARITY) return `your average price is ${amt} above the nearby average`
    if (g < -PARITY) return `your average price is ${amt} below the nearby average`
    return 'your average price is level with the nearby average'
  }

  const decisionAnswer = selected
    ? `${selected.brand} is carried by ${coveragePhrase} nearby shop${M === 1 ? '' : 's'}, and ${pricePositionClause(selected)}.`
    : ''

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* ── TOPBAR ── */}
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '0 24px', flexShrink: 0 }}>
        <div className="l-wrap" style={{ gap: 14, padding: '12px 0 10px' }}>
          <button
            onClick={() => navigate('/prices')}
            style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--accent)', cursor: 'pointer', background: 'none', border: 'none', flexShrink: 0, padding: 0 }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>
            Price Intelligence
          </button>
          <div>
            <div style={{ fontFamily: 'var(--display)', fontSize: 22, fontWeight: 400, color: 'var(--text-1)', letterSpacing: '-0.015em', lineHeight: 1 }}>
              Brand Carriers
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginLeft: 2 }}>
              {locationName.toUpperCase()} · {M} SHOPS IN RANGE · {allBrands.length} BRANDS · {radius}MI
            </div>
          </div>

          <div className="l-wrap" style={{ marginLeft: 'auto', gap: 8, justifyContent: 'flex-end' }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Radius</span>
            <select
              data-testid="bc-radius"
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              style={{ background: 'var(--surface-3)', border: '1.5px solid var(--border-2)', borderRadius: 'var(--r-sm)', padding: '6px 10px', color: 'var(--text-1)', fontSize: 11, fontFamily: 'var(--sans)', cursor: 'pointer', fontWeight: 600 }}
            >
              {[5, 10, 15, 25, 40].map((r) => <option key={r} value={r}>{r} mi</option>)}
            </select>
            {sep}
            <button
              className="btn"
              onClick={() => exportCSV(brands, M, locationName)}
              disabled={!brands.length}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 13px', fontSize: 11, opacity: brands.length ? 1 : 0.5 }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
              Export CSV
            </button>
            {locations.length > 1 && (
              <select
                data-testid="bc-location"
                value={selectedLocation || ''}
                onChange={(e) => { setSelectedLocation(e.target.value); setSelectedBrand(null) }}
                style={{ background: 'var(--surface-3)', border: '1.5px solid var(--border-2)', borderRadius: 'var(--r-sm)', padding: '7px 13px', color: 'var(--text-1)', fontSize: 11, fontFamily: 'var(--sans)', cursor: 'pointer' }}
              >
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
          </div>
        </div>
        {/* Sub-nav */}
        <div style={{ display: 'flex', alignItems: 'center', borderTop: '1px solid var(--border)', overflowX: 'auto' }}>
          {[
            { label: 'Price History', to: '/prices/history', active: false },
            { label: 'Catalog Comparison', to: '/prices/catalog', active: false },
            { label: 'Brand Carriers', to: '/prices/brands', active: true },
          ].map((tab) => (
            <div
              key={tab.label}
              onClick={() => navigate(tab.to)}
              style={{ padding: '10px 18px', fontSize: 12, fontWeight: 600, color: tab.active ? 'var(--accent)' : 'var(--text-3)', cursor: 'pointer', borderBottom: tab.active ? '2px solid var(--accent)' : '2px solid transparent', whiteSpace: 'nowrap' }}
            >
              {tab.label}
            </div>
          ))}
        </div>
      </div>

      {/* ── FILTER BAR ── */}
      <div style={{ padding: '8px 24px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
        {/* Sort */}
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Sort</span>
        <div style={{ display: 'inline-flex', background: 'var(--surface)', border: '1.5px solid var(--border-2)', borderRadius: 'var(--r-sm)', padding: 2, gap: 2 }}>
          {([
            { v: 'common' as SortMode, label: 'Most carried' },
            { v: 'rare' as SortMode, label: 'Rarest' },
            { v: 'price' as SortMode, label: 'Priciest' },
          ]).map((s) => (
            <button
              key={s.v}
              onClick={() => setSortMode(s.v)}
              style={{
                padding: '5px 12px', borderRadius: 'calc(var(--r-sm) - 2px)', fontSize: 11, fontWeight: 600,
                fontFamily: 'var(--sans)', cursor: 'pointer', border: 'none', whiteSpace: 'nowrap',
                background: sortMode === s.v ? 'var(--accent)' : 'transparent',
                color: sortMode === s.v ? '#fff' : 'var(--text-2)', transition: 'var(--transition)',
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
        {sep}
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Category</span>
        <select
          data-testid="bc-category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 'var(--r-sm)', fontSize: 11, fontWeight: 600, border: `1.5px solid ${category ? 'var(--accent)' : 'var(--border-2)'}`, background: category ? 'var(--accent)' : 'var(--surface)', color: category ? '#fff' : 'var(--text-1)', cursor: 'pointer', fontFamily: 'var(--sans)' }}
        >
          <option value="">All categories</option>
          {/* Data-driven vocabulary from the API: flower bucketed by package size
              (Flower 1g/3.5g/7g/14g/28g + general Flower), then the non-flower
              categories present in range. Strain types never appear. */}
          {categoryOptions.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        {sep}
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search a brand…"
          style={{ padding: '6px 11px', borderRadius: 'var(--r-sm)', fontSize: 11, border: '1.5px solid var(--border-2)', background: 'var(--surface)', color: 'var(--text-1)', fontFamily: 'var(--sans)', minWidth: 0, flex: '1 1 160px', maxWidth: 240 }}
        />
        <div style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)' }}>
          <span style={{ color: 'var(--warm)', fontWeight: 700 }}>{commodityCount} commodity</span> · <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{rareCount} rare</span>
        </div>
      </div>

      {/* ── SUMMARY STRIP ── */}
      <div style={{ padding: '8px 24px', borderBottom: '2px solid var(--border)', background: 'var(--surface)', display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
        {[
          { n: M, label: 'Shops in range', color: 'var(--text-1)' },
          { n: allBrands.length, label: 'Brands found', color: 'var(--accent)' },
          { n: commodityCount, label: 'Carried by most', color: 'var(--warm)' },
          { n: rareCount, label: 'Rare in area', color: 'var(--accent)' },
        ].map((s) => (
          <div key={s.label} style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface-3)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '6px 12px' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums', color: s.color }}>{s.n}</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{s.label}</div>
          </div>
        ))}
        <div style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5 }}>
          Brand = real menu data · current snapshot
        </div>
      </div>

      {/* ── CONTENT ── */}
      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontFamily: 'var(--mono)', fontSize: 13 }}>
          Mapping brand coverage across nearby shops…
        </div>
      ) : error ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--danger)', fontFamily: 'var(--mono)', fontSize: 13, padding: 24, textAlign: 'center' }}>
          {error}
          <span style={{ color: 'var(--text-3)', fontSize: 11 }}>We'll keep collecting — check back after the next window.</span>
        </div>
      ) : !selectedLocation ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: 14 }}>
          Add a location to map brand coverage.
        </div>
      ) : allBrands.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: 'var(--text-3)' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><path d="M12 8v4" /><path d="M12 16h.01" /></svg>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase' }}>No brand data in range yet</div>
          <div style={{ fontSize: 13, maxWidth: 300, textAlign: 'center' }}>
            No nearby shops with collected menus within {radius} miles. Widen the radius or check back after the next collection window.
          </div>
        </div>
      ) : (
        // minmax(420px,1.1fr) 1.3fr cannot honour its own floor below ~1000px
        // of content width, so the grid overflowed and both panes read as
        // pinched. Stepped, then stacked — see .l-listdetail.
        <div className="l-listdetail" style={{ flex: 1 }}>
          {/* Left — brand list */}
          <div className="ld-list" style={{ overflowY: 'auto', borderRight: '1px solid var(--border)', padding: '12px 14px 40px' }}>
            <SectionLabel style={{ padding: '0 6px 8px', letterSpacing: '0.12em' }}>
              {brands.length} brand{brands.length !== 1 ? 's' : ''} · {sortMode === 'common' ? 'most carried first' : sortMode === 'rare' ? 'rarest first' : 'priciest first'}
            </SectionLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {brands.map((b) => (
                <BrandListRow
                  key={b.brand_key}
                  b={b}
                  m={M}
                  selected={selected?.brand_key === b.brand_key}
                  onClick={() => setSelectedBrand(b.brand_key)}
                />
              ))}
              {brands.length === 0 && (
                <div style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--text-3)', fontSize: 12 }}>
                  No brand matches “{search}”.
                </div>
              )}
            </div>
          </div>

          {/* Right — selected brand detail */}
          <div className="ld-detail" style={{ overflowY: 'auto', padding: '18px 24px 40px' }}>
            {selected ? (
              <>
                {/* Decision summary — supported conclusion, its limits, and a
                    separate recommended action. Evidence (pills, tiles, tables)
                    follows below. */}
                <DecisionSummary
                  className="brand-carriers-summary"
                  question="Who carries this brand, and how is it priced?"
                  priority={selPriority}
                  answer={decisionAnswer}
                  impact={
                    <>This is {coveragePct}% current nearby-shop coverage. Broad availability is descriptive — it is not evidence of good or bad performance.</>
                  }
                  action={
                    selected.your_price != null ? (
                      <button
                        className="btn btn-primary"
                        data-testid="brand-compare-skus"
                        onClick={() => navigate(`/prices?view=priority&location_id=${encodeURIComponent(selectedLocation ?? '')}&brand=${encodeURIComponent(selected.brand)}`)}
                      >
                        Compare matching {selected.brand} SKUs →
                      </button>
                    ) : (
                      <button
                        className="btn"
                        data-testid="brand-investigate"
                        disabled={selected.carriers.length === 0}
                        onClick={() => {
                          const cheapest = selected.carriers[0]
                          if (cheapest) navigate(`/competitors/${cheapest.competitor_id}${selectedLocation ? `?location_id=${selectedLocation}` : ''}`)
                        }}
                      >
                        See who prices {selected.brand} lowest →
                      </button>
                    )
                  }
                >
                  <p style={{ fontSize: 12, color: 'var(--text-2)', margin: '2px 0 0', lineHeight: 1.45 }}>
                    Current snapshot · no comparable prior observation is available, so distribution change cannot be determined.
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--text-2)', margin: '4px 0 0', lineHeight: 1.45 }}>
                    Brand averages can mix different products and package sizes. Narrow the category or compare matching SKUs before repricing.
                  </p>
                  <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Pill variant="chip" tone={selected.ubiquity >= COMMODITY ? 'blocked-by' : selected.ubiquity <= RARE ? 'accent' : 'neutral'}>
                      {ubiquityTone(selected.ubiquity).label}
                    </Pill>
                    <Pill variant="chip" tone={VERDICT_TONE[selStanding]}>{VERDICT_LABEL[selStanding]}</Pill>
                    {selected.any_on_sale && <Pill variant="chip" tone="blocked-by">promo active</Pill>}
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>{selected.total_items} SKUs seen</span>
                  </div>
                </DecisionSummary>

                {/* Stat tiles */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 18 }}>
                  <StatTile size="sm" value={`${Math.round(selected.ubiquity * 100)}%`} label="Ubiquity" />
                  <StatTile size="sm" value={`$${selected.avg_price.toFixed(2)}`} label="Avg price" />
                  <StatTile
                    size="sm"
                    tone={selStanding === 'higher' ? 'danger' : selStanding === 'cheaper' ? 'accent' : 'default'}
                    value={selected.your_price != null ? `$${selected.your_price.toFixed(2)}` : '—'}
                    label="Your price"
                  />
                  <StatTile
                    size="sm"
                    tone={selStanding === 'higher' ? 'danger' : selStanding === 'cheaper' ? 'accent' : 'default'}
                    value={selected.your_price != null ? `${selected.your_price - selected.avg_price > 0 ? '+' : selected.your_price - selected.avg_price < 0 ? '−' : ''}$${Math.abs(selected.your_price - selected.avg_price).toFixed(2)}` : '—'}
                    label="vs avg"
                  />
                </div>

                {/* Carriers */}
                <SectionLabel style={{ marginBottom: 8 }}>Carrying shops · cheapest first</SectionLabel>
                <DataTable
                  rows={selected.carriers}
                  rowKey={(c) => c.competitor_id}
                  columns={[
                    {
                      key: 'name',
                      header: 'Shop',
                      mono: false,
                      render: (c) => (
                        <span
                          onClick={() => navigate(`/competitors/${c.competitor_id}${selectedLocation ? `?location_id=${selectedLocation}` : ''}`)}
                          style={{ fontWeight: 600, color: 'var(--text-1)', cursor: 'pointer' }}
                        >
                          {c.name}{c.on_sale && <span style={{ color: 'var(--warm)', marginLeft: 6, fontSize: 10, fontFamily: 'var(--mono)' }}>promo</span>}
                        </span>
                      ),
                    },
                    {
                      key: 'distance_mi',
                      header: 'Dist',
                      render: (c) => <span style={{ color: 'var(--text-3)' }}>{c.distance_mi != null ? `${c.distance_mi.toFixed(1)} mi` : '—'}</span>,
                    },
                    {
                      key: 'price',
                      header: 'Avg price',
                      render: (c) => <span style={{ fontWeight: 700, color: 'var(--text-1)' }}>${c.price.toFixed(2)}</span>,
                    },
                    {
                      key: 'vsyou',
                      header: 'vs you',
                      mono: false,
                      render: (c) => {
                        if (selected.your_price == null) return <Pill variant="chip" tone="neutral">n/a</Pill>
                        const v = youVerdict(selected.your_price, c.price)
                        const theirVsYou = c.price - selected.your_price
                        return (
                          <Pill variant="chip" tone={VERDICT_TONE[v]}>
                            {v === 'parity' ? 'even' : `${theirVsYou > 0 ? '+' : '−'}$${Math.abs(theirVsYou).toFixed(2)}`}
                          </Pill>
                        )
                      },
                    },
                  ] as Column<typeof selected.carriers[number]>[]}
                />

                {/* Non-carriers */}
                {nonCarriers.length > 0 && (
                  <>
                    <SectionLabel style={{ margin: '20px 0 8px' }}>Not carrying it · {nonCarriers.length} shop{nonCarriers.length !== 1 ? 's' : ''} in range</SectionLabel>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {nonCarriers.map((r) => (
                        <span
                          key={r.competitor_id}
                          onClick={() => navigate(`/competitors/${r.competitor_id}${selectedLocation ? `?location_id=${selectedLocation}` : ''}`)}
                          style={{ fontSize: 11.5, color: 'var(--text-2)', background: 'var(--surface-3)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '4px 10px', cursor: 'pointer' }}
                        >
                          {r.name}
                          {r.distance_mi != null && <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)', marginLeft: 6 }}>{r.distance_mi.toFixed(1)}mi</span>}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: 13 }}>
                Select a brand to see who carries it.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
